// src/theme/theme.ts
import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import { esES } from "@mui/material/locale";

// Declaramos nuestra paleta de colores
declare module "@mui/material/styles" {
  interface Palette {
    tertiary: Palette["primary"];
    neutral: Palette["primary"];
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions["primary"];
    neutral?: PaletteOptions["primary"];
  }
}

// Creamos la paleta de colores empresarial con azul como color principal
const primaryColor = {
  main: "#02447B", // Azul corporativo
  light: "#1a6aad",
  dark: "#012d52",
  contrastText: "#ffffff",
};

const secondaryColor = {
  main: "#00838f", // Azul-verde complementario
  light: "#4fb3bf",
  dark: "#005662",
  contrastText: "#ffffff",
};

const tertiaryColor = {
  main: "#0d47a1", // Azul oscuro para acentos
  light: "#5472d3",
  dark: "#002171",
  contrastText: "#ffffff",
};

const neutralColor = {
  main: "#546e7a", // Gris azulado para textos y elementos neutros
  light: "#819ca9",
  dark: "#29434e",
  contrastText: "#ffffff",
};

// Creamos el tema base
let theme = createTheme(
  {
    palette: {
      primary: primaryColor,
      secondary: secondaryColor,
      tertiary: tertiaryColor,
      neutral: neutralColor,
      background: {
        default: "#f0f4f8",
        paper: "#ffffff",
      },
      text: {
        primary: "#263238", // Texto oscuro para buen contraste
        secondary: "#546e7a", // Texto secundario más suave
      },
      error: {
        main: "#d32f2f",
      },
      warning: {
        main: "#f57c00",
      },
      info: {
        main: "#0288d1",
      },
      success: {
        main: "#388e3c",
      },
    },
    typography: {
      fontFamily: [
        "Inter",
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
      ].join(","),
      h1: {
        fontWeight: 700,
        fontSize: "2.5rem",
        lineHeight: 1.2,
        letterSpacing: "-0.01562em",
      },
      h2: {
        fontWeight: 700,
        fontSize: "2rem",
        lineHeight: 1.2,
        letterSpacing: "-0.00833em",
      },
      h3: {
        fontWeight: 600,
        fontSize: "1.75rem",
        lineHeight: 1.2,
        letterSpacing: "0em",
      },
      h4: {
        fontWeight: 600,
        fontSize: "1.5rem",
        lineHeight: 1.2,
        letterSpacing: "0.00735em",
      },
      h5: {
        fontWeight: 600,
        fontSize: "1.25rem",
        lineHeight: 1.2,
        letterSpacing: "0em",
      },
      h6: {
        fontWeight: 600,
        fontSize: "1rem",
        lineHeight: 1.2,
        letterSpacing: "0.0075em",
      },
      subtitle1: {
        fontWeight: 500,
        fontSize: "1rem",
        lineHeight: 1.5,
        letterSpacing: "0.00938em",
      },
      subtitle2: {
        fontWeight: 500,
        fontSize: "0.875rem",
        lineHeight: 1.5,
        letterSpacing: "0.00714em",
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.5,
        letterSpacing: "0.00938em",
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: 1.5,
        letterSpacing: "0.01071em",
      },
      button: {
        fontWeight: 600,
        fontSize: "0.875rem",
        lineHeight: 1.75,
        letterSpacing: "0.02857em",
        textTransform: "none", // Botones sin mayúsculas para look más moderno
      },
      caption: {
        fontSize: "0.75rem",
        lineHeight: 1.5,
        letterSpacing: "0.03333em",
      },
      overline: {
        fontSize: "0.75rem",
        lineHeight: 1.5,
        letterSpacing: "0.08333em",
        textTransform: "uppercase",
      },
    },
    shape: {
      borderRadius: 8, // Bordes ligeramente redondeados para look moderno
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: "8px 16px",
            boxShadow: "none",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            },
          },
          contained: {
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
          },
          containedPrimary: {
            background: "linear-gradient(135deg, #02447B, #1a6aad)",
            "&:hover": {
              background: "linear-gradient(135deg, #012d52, #02447B)",
              boxShadow: "0 4px 16px rgba(2, 68, 123, 0.4)",
            },
          },
          outlined: {
            borderWidth: 1.5,
            "&:hover": {
              borderWidth: 1.5,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: "0 2px 16px rgba(0, 0, 0, 0.08)",
            borderRadius: 12,
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: "20px 24px",
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: "24px",
          },
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            padding: "16px 24px",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: "none",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: "#ffffff",
            backgroundImage:
              "linear-gradient(rgba(21, 101, 192, 0.03), rgba(21, 101, 192, 0.03))",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 12,
              backgroundColor: "#f8fafc",
              transition: "box-shadow 0.2s ease",
              "&.Mui-focused": {
                boxShadow: "0 0 0 3px rgba(2, 68, 123, 0.12)",
              },
              "& fieldset": {
                borderColor: "rgba(0,0,0,0.12)",
              },
              "&:hover fieldset": {
                borderColor: "rgba(2,68,123,0.4)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#02447B",
              },
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            padding: "12px 16px",
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            "&.Mui-selected": {
              backgroundColor: `${primaryColor.light}20`,
              "&:hover": {
                backgroundColor: `${primaryColor.light}30`,
              },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: "16px 24px",
          },
          head: {
            fontWeight: 600,
            backgroundColor: `${primaryColor.light}10`,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            height: 28,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: neutralColor.dark,
            borderRadius: 6,
            padding: "8px 12px",
            fontSize: "0.75rem",
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: "rgba(0, 0, 0, 0.08)",
          },
        },
      },
    },
  },
  esES // Agregamos soporte para español
);

// Hacemos las fuentes responsive
theme = responsiveFontSizes(theme);

export default theme;
